import { Close, Icon } from "@shared/ui";
import { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { AdminContext } from "../App";
import {
  ADMIN_AVAILABLE_ROLES,
  PLATFORM_ADMIN_AVAILABLE_ROLES,
} from "../lib/constants";
import ButtonPrimary from "./ButtonPrimary";
import ButtonSecondary from "./ButtonSecondary";

/**
 * Modal for updating user permissions/role
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Function to close the modal
 * @param {Object} member - The team member being updated
 * @param {Function} onUpdate - Function to call when updating (receives newRole)
 */
const UpdatePermissionsModal = ({
  isOpen,
  onClose,
  member,
  onUpdate,
  platformClientId,
  selectedClientId,
}) => {
  const [selectedRole, setSelectedRole] = useState("");
  const userRole = useContext(AdminContext);
  // Determine available roles based on current role
  const getAvailableRoles = (currentRole) => {
    if (!currentRole) return [];
    if (selectedClientId === platformClientId) {
      if (userRole === "platform-admin") {
        // If we're currently on the platform client and we are the platform admin, then we can update the role to anything
        return PLATFORM_ADMIN_AVAILABLE_ROLES.sort();
        // If we're currently on the platform client and we are just an admin, we can only update to either member or admin
      } else if (userRole === "admin") {
        return ADMIN_AVAILABLE_ROLES.sort();
      }
    }

    // Otherwise, if we're not on the platform client, we're on a destination client
    const currentRoleLowerCase = currentRole.toLowerCase();

    if (currentRoleLowerCase === "member") {
      return ["admin"];
    } else if (currentRoleLowerCase === "admin") {
      return ["member"];
    } else {
      return [];
    }
  };

  const currentRole = member?.role || "member";

  // Memoize available roles to prevent recalculation on every render
  const availableRoles = useMemo(() => {
    if (!member) return [];
    return getAvailableRoles(member.role);
  }, [member?.role, selectedClientId, platformClientId, userRole]);

  // Set initial selected role when modal opens or member changes
  useEffect(() => {
    if (isOpen && availableRoles.length > 0) {
      setSelectedRole(availableRoles[0]);
    } else if (!isOpen) {
      // Reset selected role when modal closes
      setSelectedRole("");
    }
  }, [isOpen, availableRoles]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedRole && onUpdate) {
      onUpdate(currentRole, selectedRole);
    }
  };

  const formatRoleLabel = (role) => {
    if (!role) return "";
    return role
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (!isOpen || !member) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Update User Permissions</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 focus:outline-none"
            aria-label="Close"
          >
            <Icon icon={Close} className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-2">
              User:{" "}
              <span className="font-medium">{member.name || member.email}</span>
            </p>
            <p className="text-sm text-gray-600">
              Current Role:{" "}
              <span className="font-medium">
                {formatRoleLabel(currentRole)}
              </span>
            </p>
          </div>

          <div className="mb-6">
            <label
              htmlFor="role-select"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              New Role
            </label>
            <div className="relative">
              <select
                id="role-select"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full border border-gray-300 p-2.5 pr-10 text-sm rounded focus:outline-none focus:ring-2 focus:ring-gray-300 appearance-none bg-white"
                required
              >
                {availableRoles.map((role) => (
                  <option key={role} value={role}>
                    {formatRoleLabel(role)}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-x-3">
            <ButtonSecondary text="Cancel" onClick={onClose} type="button" />
            <ButtonPrimary text="Update Permissions" type="submit" />
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default UpdatePermissionsModal;
